import type {RenderEngineMessagingConnection} from "#render-nodes/render-engine-messaging"
import type {MeshNodes} from "#render-nodes/render-nodes"
import {gatherDataObjectReferences, gatherImageTextureNodeRefs, RenderNodes} from "#render-nodes/render-nodes"
import type {LocalFile} from "#render-nodes/types"
import type {ImageTileIndex, ImageTilesDescriptor, TypedImageData} from "@cm/utils"
import {ImageColorSpace, IsDefined, TiledImageIndex, WeightedLimit} from "@cm/utils"
import type {CmLogger} from "@cm/log"
import {NoopLogger} from "@cm/log"
import type {AtlasLayout} from "#render-nodes/helpers/tile-atlas-layout"
import {assignAtlasSlots, getAtlasSlotSize} from "#render-nodes/helpers/tile-atlas-layout"

// DataObjectIds used by the render graph are ordinarily real, always-positive database legacy ids. A
// synthetic (negative) id is used for a tiled texture's tile-index-map image, which has no corresponding
// database DataObject at all - it only needs to work as a cache key for the render engine's resource
// cache (renderer.cacheEntity), which has no database-existence validation. Keeping this convention in one
// place lets renderWithTiles() filter synthetic ids out of its generic (non-tiled) data-object handling,
// which has no notion of "synthetic" and would otherwise try to fetch/upload a real DataObject for one.
function isSyntheticAtlasDataObjectId(dataObjectId: number): boolean {
    return dataObjectId < 0
}

function getIndexMapDataObjectId(atlasDataObjectId: number): number {
    return -atlasDataObjectId
}

// Central switch for the interactive-preview tile assembly perf-investigation logging below and in
// @cm/browser-utils's assembleTiles(). Flip to true locally when investigating tile assembly perf.
export const TRACE_TILE_ASSEMBLY = false

// Per-renderer-connection record of what was last successfully assembled and uploaded for each tiled
// texture, so an update whose required tiles haven't changed (e.g. a camera-only move) can skip
// reassembling and re-uploading it entirely. Owned by the caller (one instance per renderer
// connection) rather than a module-level global: a second/reconnected renderer session has an empty
// engine-side cache and must not be told "already uploaded" based on a different session's state.
export class TileUploadState {
    clear() {
        this.signatureByDataObjectId.clear()
    }

    signatureByDataObjectId = new Map<number, string>()
}

export type DataObjectData = {contentType: string} & ({buffer: ArrayBuffer} | {localFile: LocalFile})

export type DecodeExrRequest = Uint8Array

export type DecodedExrResponse = TypedImageData

export type AssembleTilesRequest = {
    imageTilesDescriptor: ImageTilesDescriptor
    imageTileIndices: ImageTileIndex[]
    // carried purely so an assembled texture can be named in the memory accounting
    dataObjectId?: number
}

// a platform sharing the render engine's filesystem returns a file, so the engine does not hold the bytes for the whole render
export type AssembledImagePayload = ({encodedData: Uint8Array} | {localFile: LocalFile}) & {
    mediaType: string
}

export type AssembleTilesResponse = {
    // Only the tiles actually requested, packed into a dynamically-sized atlas (not a texture-sized image).
    atlas: AssembledImagePayload
    // Small packed index map (see #render-nodes/tile-index-map) that a lookup subgraph grafted onto the
    // render graph (applyAtlasLookupSurgery) uses to find which atlas slot covers a given UV.
    indexMap: AssembledImagePayload
    // Summed across all tiles processed for this texture, concurrently - can exceed the wall-clock
    // duration of the call, so treat these as relative weights rather than durations.
    tileDownloadTimeInMs: number
    tileAssemblyTimeInMs: number
    // TEMP DEBUG: optional composite/encode breakdown of tileAssemblyTimeInMs, for the interactive-preview
    // tile assembly perf investigation - only the browser platform implementation populates these.
    compositeTimeInMs?: number
    encodeTimeInMs?: number
}

// Platform-specific operations that need to be provided
export type RenderWithTilesPlatformSpecific = {
    getDataObjectData: (dataObjectId: number) => Promise<DataObjectData>

    // Makes the data object available to the render engine. Implement it to skip the work when the
    // engine already holds the object: this runs on every render, including camera-only changes.
    uploadDataObject?: (dataObjectId: number) => Promise<void>

    // Fetch tile descriptor metadata from DataObject
    getImageTilesDescriptor: (dataObjectId: number) => Promise<ImageTilesDescriptor | undefined>

    // Decode the feedback image (EXR / RGBA / float32)
    decodeExr: (request: DecodeExrRequest) => Promise<DecodedExrResponse>

    // Download the requested tiles once and assemble/encode both the atlas and its tile index map
    assembleTiles: (request: AssembleTilesRequest) => Promise<AssembleTilesResponse>
}

export type RenderWithTilesCallback = {
    statusChanged?: (status: string) => void | Promise<void>
    tileFeedbackExrAvailable?: (exrData: Blob) => void
}

export type RenderWithTilesOptions = {
    // The maximum number of screen pixels a single texel is allowed to cover (default 1.0). Fed directly
    // into the feedback shader's mip-level pick alongside tileFeedbackViewportScale - smaller than 1
    // forces a finer mip (oversampling, e.g. 0.5 = 2x oversampling), larger allows a coarser one.
    maxPixelsPerTexel?: number
    // Renders the tile-feedback pass at this fraction of the beauty pass's pixel dimensions (default 1.0,
    // i.e. full resolution) - see the viewportScale comment on FeedbackRenderOptions in
    // createFeedbackRenderGraph for why a smaller feedback pass is both cheap and still correct.
    tileFeedbackViewportScale?: number
    // Caps the finest tile mip level the feedback shader will ever request, expressed as a target pixel
    // size (e.g. 2000 = never request tiles finer than needed to cover a 2000px texture) - mirrors the
    // real-time three.js tiled texture manager's minMipLevel. 0 or omitted = uncapped (always finest).
    maxTextureResolution?: number
    logger?: CmLogger
    // Bounds total estimated memory (bytes) held by textures assembling concurrently - each one holds a
    // full outputWidth * outputHeight canvas plus its own live pixel-readback/encode buffers at once, and
    // texture sizes vary hugely, so this is far more accurate than a flat task-count limit for keeping
    // peak memory bounded on low-spec machines. Default: 1GiB.
    textureAssemblyMemoryBudgetBytes?: number
    // Tracks what's already been assembled/uploaded to this renderer connection so unchanged textures
    // (e.g. after a camera-only move) can skip reassembly and re-upload entirely. Create one instance per
    // renderer connection and pass the same one on every call; omit to always reassemble (current/legacy
    // behavior, e.g. the backend render-task path where this isn't worth the complexity).
    tileUploadState?: TileUploadState
}

// Download and assembly happen interleaved per-tile (each tile is downloaded then immediately composited,
// concurrently with other tiles), so their summed durations can exceed the phase's wall-clock time. tileDownloadTimeInMs
// and tileAssemblyTimeInMs are the wall-clock time of the phase split proportionally by the download-vs-assembly
// ratio of those summed durations, so they always add up to the phase's actual wall-clock time.
export type RenderWithTilesResult = {
    nonTiledDownloadTimeInMs: number
    tileFeedbackTimeInMs: number
    tileDownloadTimeInMs: number
    tileAssemblyTimeInMs: number
}

export async function renderWithTiles(
    renderer: RenderEngineMessagingConnection<any>,
    renderGraph: RenderNodes.Render,
    platform: RenderWithTilesPlatformSpecific,
    callback?: RenderWithTilesCallback,
    options?: RenderWithTilesOptions,
): Promise<RenderWithTilesResult> {
    const statusUpdate = async (status: string) => callback?.statusChanged?.(status)
    const maxPixelsPerTexel = options?.maxPixelsPerTexel ?? 0.5 // 2x oversampling by default
    const tileFeedbackViewportScale = options?.tileFeedbackViewportScale ?? 1.0
    const maxTextureResolution = options?.maxTextureResolution ?? 0
    const logger = options?.logger ?? NoopLogger
    const textureAssemblyMemoryBudgetBytes = options?.textureAssemblyMemoryBudgetBytes ?? 1024 * 1024 * 1024
    const tileUploadState = options?.tileUploadState
    await statusUpdate("Querying data objects")
    // Synthetic (negative) ids belong to tile-index-map nodes that graph surgery (see applyAtlasLookupSurgery)
    // grafts onto a per-call CLONE of the scene below, never onto the cached/shared graph itself - so they
    // cannot appear in the render graph handed to this function. Filtered defensively anyway: they have no
    // real backing DataObject, so they must never reach the generic non-tiled-data-object upload path below.
    const dataObjectIds = gatherDataObjectReferences(renderGraph).filter((id) => !isSyntheticAtlasDataObjectId(id))
    // Only DataObjects referenced by `image_texture` shader nodes are eligible for tiling - this excludes
    // environment/HDRI maps (`environment_texture` nodes), which the feedback pass never samples and whose
    // tiles are lossy-compressed, clipping the HDR range needed for lighting.
    const imageTextureNodeRefs = gatherImageTextureNodeRefs(renderGraph).filter((ref) => !isSyntheticAtlasDataObjectId(ref.dataObjectId))
    const imageTextureDataObjectIds = Array.from(new Set(imageTextureNodeRefs.map((ref) => ref.dataObjectId)))
    const optionalImageTilesDescriptorByDataObjectId = await getImageTilesDescriptorMap(platform, imageTextureDataObjectIds)
    const imageTilesDescriptorDefined = (value: [number, ImageTilesDescriptorEx | undefined]): value is [number, ImageTilesDescriptorEx] =>
        value[1] !== undefined
    const imageTilesDescriptorByDataObjectId = new Map(Array.from(optionalImageTilesDescriptorByDataObjectId.entries()).filter(imageTilesDescriptorDefined))
    const nonImageTilesDataObjectIds = dataObjectIds.filter((dataObjectId) => !imageTilesDescriptorByDataObjectId.has(dataObjectId))
    await statusUpdate("Updating data objects")
    const nonTiledDownloadStart = performance.now()
    await uploadNonTiledDataObjects(renderer, platform, nonImageTilesDataObjectIds)
    const nonTiledDownloadTimeInMs = performance.now() - nonTiledDownloadStart
    let tileFeedbackTimeInMs = 0
    let tileDownloadTimeInMs = 0
    let tileAssemblyTimeInMs = 0

    let graphToRender = renderGraph
    if (imageTilesDescriptorByDataObjectId.size > 0) {
        await statusUpdate("Querying texture tiles")
        const feedbackStart = performance.now()
        const textureTileFeedbackResult = await renderTextureTileFeedbackPass(
            renderer,
            renderGraph,
            tileFeedbackViewportScale,
            maxPixelsPerTexel,
            maxTextureResolution,
            platform,
            callback,
            imageTilesDescriptorByDataObjectId,
            logger,
        )
        tileFeedbackTimeInMs = performance.now() - feedbackStart

        await statusUpdate("Updating texture tiles")
        const assembleStart = performance.now()
        const {
            tileDownloadTimeInMs: rawTileDownloadTimeInMs,
            tileAssemblyTimeInMs: rawTileAssemblyTimeInMs,
            atlasLayoutByDataObjectId,
        } = await assembleAndUploadTiledTextures(renderer, platform, textureTileFeedbackResult, logger, textureAssemblyMemoryBudgetBytes, tileUploadState)
        const totalAssembleTimeInMs = performance.now() - assembleStart
        const rawTotalTimeInMs = rawTileDownloadTimeInMs + rawTileAssemblyTimeInMs
        const downloadFraction = rawTotalTimeInMs > 0 ? rawTileDownloadTimeInMs / rawTotalTimeInMs : 0
        tileDownloadTimeInMs = downloadFraction * totalAssembleTimeInMs
        tileAssemblyTimeInMs = totalAssembleTimeInMs - tileDownloadTimeInMs

        graphToRender = patchRenderGraph(renderGraph, imageTilesDescriptorByDataObjectId, atlasLayoutByDataObjectId)
    }

    await statusUpdate("Rendering")
    await renderer.update(graphToRender)

    return {
        nonTiledDownloadTimeInMs,
        tileFeedbackTimeInMs,
        tileDownloadTimeInMs,
        tileAssemblyTimeInMs,
    }
}

type TextureTileFeedbackResult = {
    textureTilesRequests: TextureTilesRequest[]
}

type ImageTilesDescriptorEx = ImageTilesDescriptor & {
    textureId: number
}

type TextureTilesRequest = {
    dataObjectId: number
    imageTilesDescriptor: ImageTilesDescriptorEx
    requestedTiles: ImageTileIndex[]
}

async function uploadNonTiledDataObjects(
    renderer: RenderEngineMessagingConnection<any>,
    platform: RenderWithTilesPlatformSpecific,
    dataObjectIds: number[],
): Promise<void> {
    await Promise.all(
        dataObjectIds.map(async (dataObjectId) => {
            if (platform.uploadDataObject) return platform.uploadDataObject(dataObjectId)
            const dataObjectData = await platform.getDataObjectData(dataObjectId)
            await renderer.cacheEntity({
                type: "dataObject",
                dataObjectId,
                contentType: dataObjectData.contentType,
                ...("localFile" in dataObjectData ? {localFile: dataObjectData.localFile} : {data: new Uint8Array(dataObjectData.buffer)}),
            })
        }),
    )
}

// Bytes/pixel and the number of same-sized buffers realistically alive at once while assembling a
// texture (atlas canvas backing store + getImageData() readback + encoded output buffer - see
// assembleTiles in @cm/browser-utils), used to turn a texture's atlas output size into an estimated
// memory weight.
const ASSEMBLY_BYTES_PER_PIXEL = 4
const ASSEMBLY_LIVE_BUFFERS_PER_TEXTURE = 3

async function assembleAndUploadTiledTextures(
    renderer: RenderEngineMessagingConnection<any>,
    platform: RenderWithTilesPlatformSpecific,
    textureTileFeedbackResult: TextureTileFeedbackResult,
    logger: CmLogger,
    textureAssemblyMemoryBudgetBytes: number,
    tileUploadState: TileUploadState | undefined,
): Promise<{tileDownloadTimeInMs: number; tileAssemblyTimeInMs: number; atlasLayoutByDataObjectId: Map<number, AtlasLayout>}> {
    let tileDownloadTimeInMs = 0
    let tileAssemblyTimeInMs = 0
    // TEMP DEBUG: aggregate stage timings/counts for the interactive-preview tile assembly perf
    // investigation, logged as one summary line once every texture has been handled.
    let totalCompositeTimeInMs = 0
    let totalEncodeTimeInMs = 0
    let totalCacheEntityTimeInMs = 0
    let totalPayloadBytes = 0
    let assembledCount = 0
    let skippedCount = 0
    const assembleLimit = new WeightedLimit(textureAssemblyMemoryBudgetBytes)
    const atlasLayoutByDataObjectId = new Map<number, AtlasLayout>()
    // assemble texture from tiles
    await Promise.all(
        textureTileFeedbackResult.textureTilesRequests.map(async (textureTilesRequest) => {
            const descriptor = textureTilesRequest.imageTilesDescriptor
            // Remove all tiles which are fully covered by higher-detail tiles, then drop any tile index
            // feedback inaccuracies pushed out of range. This validity filter MUST run here, before
            // assignAtlasSlots/platform.assembleTiles - both of them, and the shader-graph surgery below,
            // need to agree on the exact same tile set, or the atlas pixel dimensions patched into the
            // shader graph (from assignAtlasSlots here) end up describing a different-sized atlas than the
            // one platform.assembleTiles actually built (if it did its own, separately-filtered validity
            // check instead), scrambling every UV computed against it. Previously this filtering happened
            // independently inside each platform's assembleTiles (isValidTileIndex/isValidTile - confirmed
            // to be the exact same check), which is exactly the scenario that could disagree with this
            // file's own atlas-layout computation; it's now done once, here, and the platform
            // implementations trust the (now already-valid) tiles they're given.
            const tiledImageIndex = TiledImageIndex.fromDescriptor(descriptor)
            const requestedTiles = removeCoveredTiles(textureTilesRequest.requestedTiles).filter((tile) => tiledImageIndex.isValidTileIndex(tile))
            const signature = computeTileSetSignature(requestedTiles)

            // Cheap, pure layout math - computed unconditionally (even when the expensive
            // download/composite/encode below is skipped) so the caller always has a fresh atlas layout to
            // graft the shader-graph lookup subgraph onto (applyAtlasLookupSurgery runs every call, not just
            // when reassembly happened - see its own comment).
            const atlasLayout = assignAtlasSlots(requestedTiles)
            atlasLayoutByDataObjectId.set(textureTilesRequest.dataObjectId, atlasLayout)

            const previousSignature = tileUploadState?.signatureByDataObjectId.get(textureTilesRequest.dataObjectId)
            if (previousSignature === signature) {
                // nothing changed
                skippedCount++
                return
            }

            // estimate output size from the atlas (bounded by requested-tile count), not the full texture
            const slotSize = getAtlasSlotSize(descriptor)
            const outputWidth = atlasLayout.cols * slotSize.width
            const outputHeight = atlasLayout.rows * slotSize.height
            const estimatedWeight = outputWidth * outputHeight * ASSEMBLY_BYTES_PER_PIXEL * ASSEMBLY_LIVE_BUFFERS_PER_TEXTURE

            await assembleLimit.run(estimatedWeight, async () => {
                logger.trace(`Assembling data-object ${textureTilesRequest.dataObjectId} from ${requestedTiles.length} tiles`)
                const assembledTextureResponse = await platform.assembleTiles({
                    imageTilesDescriptor: descriptor,
                    imageTileIndices: requestedTiles,
                    dataObjectId: textureTilesRequest.dataObjectId,
                })
                tileDownloadTimeInMs += assembledTextureResponse.tileDownloadTimeInMs
                tileAssemblyTimeInMs += assembledTextureResponse.tileAssemblyTimeInMs
                // cache both images in the render engine's resource cache
                // TEMP DEBUG: remove once the interactive-preview tile assembly perf investigation is done
                const cacheEntityStart = performance.now()
                await Promise.all([
                    renderer.cacheEntity({
                        type: "dataObject",
                        dataObjectId: textureTilesRequest.dataObjectId,
                        contentType: assembledTextureResponse.atlas.mediaType,
                        ...("localFile" in assembledTextureResponse.atlas
                            ? {localFile: assembledTextureResponse.atlas.localFile}
                            : {data: assembledTextureResponse.atlas.encodedData}),
                    }),
                    renderer.cacheEntity({
                        type: "dataObject",
                        dataObjectId: getIndexMapDataObjectId(textureTilesRequest.dataObjectId),
                        contentType: assembledTextureResponse.indexMap.mediaType,
                        ...("localFile" in assembledTextureResponse.indexMap
                            ? {localFile: assembledTextureResponse.indexMap.localFile}
                            : {data: assembledTextureResponse.indexMap.encodedData}),
                    }),
                ])
                const cacheEntityTimeInMs = performance.now() - cacheEntityStart
                const payloadBytes =
                    ("encodedData" in assembledTextureResponse.atlas ? assembledTextureResponse.atlas.encodedData.byteLength : 0) +
                    ("encodedData" in assembledTextureResponse.indexMap ? assembledTextureResponse.indexMap.encodedData.byteLength : 0)
                if (TRACE_TILE_ASSEMBLY) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `[tile-assembly] dataObject=${textureTilesRequest.dataObjectId} cacheEntity payloadBytes=${payloadBytes} ` +
                            `cacheEntity=${cacheEntityTimeInMs.toFixed(1)}ms`,
                    )
                }
                totalCompositeTimeInMs += assembledTextureResponse.compositeTimeInMs ?? 0
                totalEncodeTimeInMs += assembledTextureResponse.encodeTimeInMs ?? 0
                totalCacheEntityTimeInMs += cacheEntityTimeInMs
                totalPayloadBytes += payloadBytes
                assembledCount++
            })

            tileUploadState?.signatureByDataObjectId.set(textureTilesRequest.dataObjectId, signature)
        }),
    )
    if (TRACE_TILE_ASSEMBLY) {
        // eslint-disable-next-line no-console
        console.log(
            `[tile-assembly] TOTAL textures=${textureTileFeedbackResult.textureTilesRequests.length} assembled=${assembledCount} skipped=${skippedCount} ` +
                `download=${tileDownloadTimeInMs.toFixed(1)}ms composite=${totalCompositeTimeInMs.toFixed(1)}ms encode=${totalEncodeTimeInMs.toFixed(1)}ms ` +
                `cacheEntity=${totalCacheEntityTimeInMs.toFixed(1)}ms payloadBytes=${totalPayloadBytes}`,
        )
    }
    return {
        tileDownloadTimeInMs,
        tileAssemblyTimeInMs,
        atlasLayoutByDataObjectId,
    }
}

// replaces the tiled texture image nodes with the corresponding atlas lookup subgraph
function patchRenderGraph(
    renderGraph: RenderNodes.Render,
    imageTilesDescriptorByDataObjectId: Map<number, ImageTilesDescriptorEx>,
    atlasLayoutByDataObjectId: Map<number, AtlasLayout>,
): RenderNodes.Render {
    const clonedRenderGraph = cloneMaterialShaderGraphs(renderGraph)
    for (const {dataObjectId, node} of gatherImageTextureNodeRefs(clonedRenderGraph)) {
        const descriptor = imageTilesDescriptorByDataObjectId.get(dataObjectId)
        const atlasLayout = atlasLayoutByDataObjectId.get(dataObjectId)
        if (descriptor && atlasLayout) {
            node.parameters.colorspace = descriptor.tileColorSpace === ImageColorSpace.Srgb ? "__builtin_srgb" : "__builtin_raw"
            applyAtlasLookupSurgery(node, dataObjectId, descriptor, atlasLayout)
        }
    }
    return clonedRenderGraph
}

async function renderTextureTileFeedbackPass(
    renderer: RenderEngineMessagingConnection<any>,
    renderGraph: RenderNodes.Render,
    viewportScale: number,
    maxPixelsPerTexel: number,
    maxTextureResolution: number,
    platform: RenderWithTilesPlatformSpecific,
    callback: RenderWithTilesCallback | undefined,
    tiledTextureDescriptorByDataObjectId: Map<number, ImageTilesDescriptorEx>,
    logger: CmLogger,
): Promise<TextureTileFeedbackResult> {
    if (logger.isEnabled("debug")) {
        logger.debug(`Found ${tiledTextureDescriptorByDataObjectId.size} tiled textures:`)
        tiledTextureDescriptorByDataObjectId.forEach((descriptor, dataObjectId) => {
            logger.debug(
                `  - DataObject ${dataObjectId}: textureId=${descriptor.textureId}, ${descriptor.imageWidth}x${descriptor.imageHeight}, ${descriptor.tileMipLevels} mips`,
            )
        })
    }

    // Create feedback render graph
    const feedbackGraph = createFeedbackRenderGraph({
        renderGraph: renderGraph,
        tiledTextures: tiledTextureDescriptorByDataObjectId,
        viewportScale,
        maxPixelsPerTexel,
        maxTextureResolution,
    })

    // Render feedback pass
    logger.debug("Rendering feedback pass...")
    const feedbackRenderStart = performance.now()
    await renderer.update(feedbackGraph)

    // Wait for render to complete
    let status = await renderer.getStatus()
    while (status.state !== "finished" && status.state !== "error") {
        await new Promise((resolve) => setTimeout(resolve, 50))
        status = await renderer.getStatus()
    }

    if (status.state === "error") {
        throw new Error(`[Tile Feedback] Render failed: ${status.message}`)
    }

    logger.debug(`Feedback pass complete (${(performance.now() - feedbackRenderStart).toFixed(0)} ms), fetching image...`)

    // Get the feedback image
    const feedbackImage = await renderer.getFinalImage("Combined", "exr32")

    logger.debug(
        `Feedback image: ${feedbackImage.width}x${feedbackImage.height}, ${feedbackImage.components} components, ${feedbackImage.dataType}, ${feedbackImage.data.byteLength} bytes`,
    )

    callback?.tileFeedbackExrAvailable?.(
        new Blob([new Uint8Array(feedbackImage.data.buffer as ArrayBuffer, feedbackImage.data.byteOffset, feedbackImage.data.byteLength)], {
            type: "image/x-exr",
        }),
    )

    // Decode tile requests
    logger.debug("Decoding feedback image...")
    const decodedFeedbackImage = await platform.decodeExr(
        new Uint8Array(feedbackImage.data.buffer, feedbackImage.data.byteOffset, feedbackImage.data.byteLength),
    )

    logger.debug("Gathering tile requests...")
    const maxTextureId = Array.from(tiledTextureDescriptorByDataObjectId.values()).reduce((maxId, desc) => Math.max(maxId, desc.textureId), 0)
    const tileRequests = decodeTileRequests(decodedFeedbackImage, maxTextureId, logger)

    logger.debug(`Gathered ${tileRequests.length} tile requests:`)

    // Group by texture for better logging
    const dataObjectIdByTextureId = new Map<number, number>()
    tiledTextureDescriptorByDataObjectId.forEach((descriptor, dataObjectId) => {
        const setDataObjectId = dataObjectIdByTextureId.get(descriptor.textureId)
        if (setDataObjectId !== undefined) {
            if (setDataObjectId !== dataObjectId) {
                throw new Error(
                    `[Tile Feedback] Texture ID ${descriptor.textureId} is assigned to multiple DataObjects: ${setDataObjectId} and ${dataObjectId}`,
                )
            }
        } else {
            dataObjectIdByTextureId.set(descriptor.textureId, dataObjectId)
        }
    })

    // we always request at least the coarsest mip level (0,0)
    const textureTilesRequestByDataObjectId = new Map<number, TextureTilesRequest>()
    for (const [dataObjectId, tiledTextureDescriptor] of tiledTextureDescriptorByDataObjectId) {
        textureTilesRequestByDataObjectId.set(dataObjectId, {
            dataObjectId,
            imageTilesDescriptor: tiledTextureDescriptor,
            requestedTiles: [
                {
                    mipLevel: tiledTextureDescriptor.tileMipLevels - 1,
                    tileIndex: {x: 0, y: 0},
                },
            ],
        })
    }
    for (const request of tileRequests) {
        const dataObjectId = dataObjectIdByTextureId.get(request.textureId)
        if (!dataObjectId) {
            throw new Error(`[Tile Feedback] No DataObject found for texture ID ${request.textureId}`)
        }
        const textureTilesRequest = textureTilesRequestByDataObjectId.get(dataObjectId)
        if (!textureTilesRequest) {
            throw new Error(`[Tile Feedback] No TextureTilesRequest found for DataObject ID ${dataObjectId}`)
        }
        textureTilesRequest.requestedTiles.push({
            mipLevel: request.mipLevel,
            tileIndex: {x: request.tileX, y: request.tileY},
        })
    }

    if (logger.isEnabled("debug")) {
        textureTilesRequestByDataObjectId.forEach((textureTilesRequest, dataObjectId) => {
            logger.debug(`  DataObject ${dataObjectId}: ${textureTilesRequest.requestedTiles.length} tiles`)
            const byMipLevel = new Map<number, number>()
            for (const req of textureTilesRequest.requestedTiles) {
                byMipLevel.set(req.mipLevel, (byMipLevel.get(req.mipLevel) || 0) + 1)
            }
            byMipLevel.forEach((count, mipLevel) => {
                logger.debug(`    Mip ${mipLevel}: ${count} tiles`)
            })
        })
    }

    return {
        textureTilesRequests: Array.from(textureTilesRequestByDataObjectId.values()),
    }
}

async function getImageTilesDescriptorMap(
    platform: RenderWithTilesPlatformSpecific,
    dataObjectIds: number[],
): Promise<Map<number, ImageTilesDescriptorEx | undefined>> {
    let textureId = 1
    return new Map(
        await Promise.all(
            dataObjectIds.map(async (dataObjectId): Promise<[number, ImageTilesDescriptorEx | undefined]> => {
                const imageTilesDescriptor = await platform.getImageTilesDescriptor(dataObjectId)
                return [
                    dataObjectId,
                    imageTilesDescriptor
                        ? {
                              textureId: textureId++,
                              imageWidth: imageTilesDescriptor.imageWidth,
                              imageHeight: imageTilesDescriptor.imageHeight,
                              tileWidth: imageTilesDescriptor.tileWidth,
                              tileHeight: imageTilesDescriptor.tileHeight,
                              tileChannels: imageTilesDescriptor.tileChannels,
                              tileMipLevels: imageTilesDescriptor.tileMipLevels,
                              tileMargin: imageTilesDescriptor.tileMargin,
                              tileColorSpace: imageTilesDescriptor.tileColorSpace,
                              tileMediaType: imageTilesDescriptor.tileMediaType,
                              tileDownloadUrlPrefix: imageTilesDescriptor.tileDownloadUrlPrefix,
                          }
                        : undefined,
                ]
            }),
        ),
    )
}

function removeCoveredTiles(tileRequests: ImageTileIndex[]): ImageTileIndex[] {
    const makeKey = (mipLevel: number, tileX: number, tileY: number): string => `${mipLevel}:${tileX}:${tileY}`
    const tileRequestByKey = new Map<string, ImageTileIndex>()
    tileRequests.forEach((tileRequest) => tileRequestByKey.set(makeKey(tileRequest.mipLevel, tileRequest.tileIndex.x, tileRequest.tileIndex.y), tileRequest))
    const filteredRequestedTiles: ImageTileIndex[] = []
    const isCoveredByKey = new Map<string, boolean>()
    const processTile = (mipLevel: number, tileX: number, tileY: number): boolean => {
        const key = makeKey(mipLevel, tileX, tileY)
        let isCovered = isCoveredByKey.get(key)
        if (isCovered !== undefined) {
            return isCovered
        }
        const tileRequest = tileRequestByKey.get(key)
        if (mipLevel > 0) {
            // check if finer tiles exist
            const finerMipLevel = mipLevel - 1
            const finerTileX0 = tileX * 2
            const finerTileY0 = tileY * 2
            let allCovered = true
            for (let offsetY = 0; offsetY < 2; offsetY++) {
                for (let offsetX = 0; offsetX < 2; offsetX++) {
                    if (!processTile(finerMipLevel, finerTileX0 + offsetX, finerTileY0 + offsetY)) {
                        allCovered = false
                    }
                }
            }
            isCovered = allCovered
        }
        if (!isCovered) {
            if (tileRequest) {
                isCovered = true
                filteredRequestedTiles.push(tileRequest)
            } else {
                isCovered = false
            }
        }
        tileRequestByKey.delete(key)
        isCoveredByKey.set(key, isCovered)
        return isCovered
    }
    while (tileRequestByKey.size > 0) {
        const tileRequest = tileRequestByKey.values().next().value as ImageTileIndex
        processTile(tileRequest.mipLevel, tileRequest.tileIndex.x, tileRequest.tileIndex.y)
    }
    return filteredRequestedTiles
}

function computeTileSetSignature(tiles: ImageTileIndex[]): string {
    return tiles
        .map((tile) => `${tile.mipLevel}:${tile.tileIndex.x}:${tile.tileIndex.y}`)
        .sort()
        .join(",")
}

// --- Atlas + tile-index-map lookup subgraph -------------------------------------------------------------
//
// Replaces a single `image_texture` node's implicit "sample the whole flat texture" behavior with: sample
// the small tile-index-map texture (nearest, to get an exact packed byte triple) -> decode which atlas slot
// (and at which mip level) holds the relevant tile -> compute the atlas UV (slot offset + margin + the
// within-tile fractional position, corrected for the stored tile's mip level) -> feed that computed UV into
// the tracked node's own `vector` input, so it now samples the atlas instead of a flat image. No OSL is
// used anywhere in this subgraph (OSL only executes on CPU in Cycles) - every node type here compiles
// through Cycles' SVM (Shader Virtual Machine) and runs on GPU.

type NodeInput = [RenderNodes.ShaderNode, string]

function mathNode(
    mathType: string,
    values: {value1?: NodeInput; value2?: NodeInput; value3?: NodeInput},
    constants: {value1?: number; value2?: number; value3?: number} = {},
): RenderNodes.ShaderNode {
    return {
        type: "math",
        inputs: {
            ...values,
        },
        parameters: {
            math_type: mathType,
            ...constants,
        },
    }
}

function vectorMathNode(
    mathType: string,
    values: {vector1?: NodeInput; vector2?: NodeInput; vector3?: NodeInput; scale?: NodeInput},
    constants: {vector1?: [number, number, number]; vector2?: [number, number, number]; vector3?: [number, number, number]; scale?: number} = {},
): RenderNodes.ShaderNode {
    return {
        type: "vector_math",
        inputs: {
            ...values,
        },
        parameters: {
            math_type: mathType,
            ...constants,
        },
    }
}

function separateRgb(color: NodeInput): RenderNodes.ShaderNode {
    return {
        type: "separate_rgb",
        inputs: {
            color,
        },
        parameters: {},
    }
}

function combineXyz(x: NodeInput | number, y: NodeInput | number, z: NodeInput | number = 0): RenderNodes.ShaderNode {
    const inputs: RenderNodes.ShaderNode["inputs"] = {}
    const parameters: Record<string, unknown> = {}
    const assign = (key: "x" | "y" | "z", value: NodeInput | number) => {
        if (typeof value === "number") parameters[key] = value
        else inputs[key] = value
    }
    assign("x", x)
    assign("y", y)
    assign("z", z)
    return {
        type: "combine_xyz",
        inputs,
        parameters,
    }
}

function atlasImageTextureNode(vector: NodeInput, dataObjectId: number, interpolation: string, extension: string): RenderNodes.ShaderNode {
    return {
        type: "image_texture",
        inputs: {vector},
        parameters: {colorspace: "__builtin_raw", interpolation, extension, projection: "flat"},
        resources: {image: RenderNodes.loadImage(dataObjectId)},
    }
}

// The feedback-pass shader builder (createFeedbackShaderForMaterial, via getTextureUvInput below) needs a
// tiled texture node's material UV. Safe to read `node.inputs.vector` directly here: the feedback pass always
// runs against the original, pristine graph (renderTextureTileFeedbackPass is called before the scene is
// cloned and surgically modified for the real render - see renderWithTiles), so it never observes a node
// surgery has already rewired.

// Grafts the atlas + tile-index-map double-lookup subgraph onto an `image_texture` node in place, so what was
// a single texture lookup in the material becomes: sample the tile index map (nearest) -> decode the packed
// atlas slot + mip level -> compute the atlas UV -> sample the atlas. Callers must pass a per-call CLONE of
// the node (see cloneMaterialShaderGraphs in renderWithTiles) - this function always rebuilds the subgraph
// from scratch and mutates the given node directly, so it must never be called with a node shared with a
// cache or with any other render.
export function applyAtlasLookupSurgery(node: RenderNodes.ShaderNode, dataObjectId: number, descriptor: ImageTilesDescriptor, atlasLayout: AtlasLayout): void {
    const slotSize = getAtlasSlotSize(descriptor)
    const atlasPixelWidth = atlasLayout.cols * slotSize.width
    const atlasPixelHeight = atlasLayout.rows * slotSize.height

    const tiledImageIndex = TiledImageIndex.fromDescriptor(descriptor)
    // The index map TEXTURE has an integer pixel grid, ceil(imageSize/tileSize) per axis (one texel per
    // tile, including a texel for a partial last tile) - only ITS addressing (indexMapUv below) should use
    // this.
    const numTilesXCeil = tiledImageIndex.numTilesX
    const numTilesYCeil = tiledImageIndex.numTilesY
    // But the UV->tile-grid math itself (which tile index a UV falls into, and the fraction within it) must
    // use the TRUE, non-integer ratio imageSize/tileSize - exactly matching the tile-feedback OSL shader's
    // own `numTilesX = textureSizeX / tileSizeX` (see getOslData's surfaceSampling code, further down this
    // file). The tile server does not pad the source image to a tile-size multiple (confirmed in
    // tile_helpers.py), so the last tile column/row genuinely has less than tileWidth/tileHeight of real
    // content, and assembleTiles pads that shortfall with edge-clamped pixels to keep every atlas slot a
    // uniform size. Using the ceiling here (as an earlier version of this code did) would treat that
    // partial tile as if it were full-width, extending the addressable UV range for it past where the real
    // content ends and into the edge-clamp padding - visible as the pattern cutting off/smearing near that
    // tile's far edge instead of continuing into the next tile. The true ratio makes the last tile's
    // fraction naturally stop short of 1.0, at exactly the point its real content actually ends.
    const numTilesXRatio = descriptor.imageWidth / descriptor.tileWidth
    const numTilesYRatio = descriptor.imageHeight / descriptor.tileHeight

    // Capture the ORIGINAL uv input (or the implicit default) before overwriting it below.
    const origUv: NodeInput = node.inputs.vector ?? [createDefaultUvNode(), "UV"]
    const extension = node.parameters.extension

    // 1. Wrap the original UV into [0,1) per the texture's own extension/wrap mode. This happens explicitly
    // here (not left to the image nodes' own wrap handling) so the SAME wrapped UV drives both the
    // index-map lookup and the atlas lookup consistently. "black"/"mirror" are approximated as "clamp" for
    // addressing purposes (never out of range) - this doesn't reproduce mirror's exact reflected tiling or
    // black's hard edge, deferred as a low-priority refinement since periodic/clamp dominate real usage.
    let wrappedUv: NodeInput
    if (extension === "periodic") {
        const origSep: RenderNodes.ShaderNode = {type: "separate_xyz", inputs: {vector: origUv}, parameters: {}}
        const uWrap = mathNode("floored_modulo", {value1: [origSep, "x"]}, {value2: 1})
        const vWrap = mathNode("floored_modulo", {value1: [origSep, "y"]}, {value2: 1})
        const combined = combineXyz([uWrap, "value"], [vWrap, "value"], 0)
        wrappedUv = [combined, "vector"]
    } else {
        const clampedHigh = vectorMathNode("minimum", {vector1: origUv}, {vector2: [0.999999, 0.999999, 1]})
        const clampedLow = vectorMathNode("maximum", {vector1: [clampedHigh, "vector"]}, {vector2: [0, 0, 0]})
        wrappedUv = [clampedLow, "vector"]
    }

    // 1b. Convert to file-row space ONCE, right here, and keep every intermediate quantity below in that
    // same space consistently - no further identities are relied on to keep things aligned. Cycles' V
    // increases upward; file rows increase downward (row 0 = top, matching both how assembleTiles blits
    // tile pixels and how the tile-feedback OSL shader's own tileY = floor((1-v)*numTilesY) is defined, see
    // getOslData's surfaceSampling code further down this file) - so Y needs `1 - v` exactly once here. X is
    // never flipped, by Cycles or by the feedback shader, so it passes through unchanged. Do NOT try to fold
    // this into gridUv's scale/divide below or skip it by relying on cancellation with the read-time flips
    // at the two image_texture samples further down (indexTexel, and the tracked node itself) - that
    // approach only holds when the tile-grid ratio is a whole number, which is false for any texture whose
    // last tile is partial (i.e. most real textures).
    const wrappedSep: RenderNodes.ShaderNode = {type: "separate_xyz", inputs: {vector: wrappedUv}, parameters: {}}
    const fileRowV = mathNode("subtract", {value2: [wrappedSep, "y"]}, {value1: 1})
    const fileRowUv = combineXyz([wrappedSep, "x"], [fileRowV, "value"], 0)

    // 2. Continuous tile-grid coordinate, shared by both branches below (safe to fan out - both the TS
    // graph walk and the C++ traverseShaderNodes dedupe visited nodes by object identity). Uses the TRUE
    // ratio, not the index map's ceiling'd pixel-grid size - see the comment above numTilesXRatio. gridUv.y
    // now matches the feedback shader's own tileY calculation exactly (both operate on `1-v`).
    const gridUv = vectorMathNode("multiply", {vector1: [fileRowUv, "vector"]}, {vector2: [numTilesXRatio, numTilesYRatio, 1]})

    // Branch A: index-map lookup (pixel-centered UV into the mip-0 grid) and decode
    const tileIndexFloor = vectorMathNode("floor", {vector1: [gridUv, "vector"]})
    const tileIndexCentered = vectorMathNode("add", {vector1: [tileIndexFloor, "vector"]}, {vector2: [0.5, 0.5, 0]})
    // Addressing the index map TEXTURE itself, so this divide uses its actual (ceiling'd) pixel dimensions.
    // Still in file-row space at this point.
    const indexMapUvFileRow = vectorMathNode("divide", {vector1: [tileIndexCentered, "vector"]}, {vector2: [numTilesXCeil, numTilesYCeil, 1]})
    // Explicit compensating flip for THIS specific image_texture sample - Cycles re-flips Y independently
    // for every image_texture node it evaluates, so every sample needs its own compensation at the point it
    // happens, not just once globally.
    const indexMapUvSep: RenderNodes.ShaderNode = {type: "separate_xyz", inputs: {vector: [indexMapUvFileRow, "vector"]}, parameters: {}}
    const indexMapFlippedV = mathNode("subtract", {value2: [indexMapUvSep, "y"]}, {value1: 1})
    const indexMapUv = combineXyz([indexMapUvSep, "x"], [indexMapFlippedV, "value"], 0)
    // "closest"/"clamp" are mandatory here regardless of the texture's own settings: bilinear filtering
    // would blend adjacent packed-integer texels into meaningless values, and this lookup must never sample
    // outside the index map (wrapping is already handled above, on the UV feeding into it).
    const indexTexel = atlasImageTextureNode([indexMapUv, "vector"], getIndexMapDataObjectId(dataObjectId), "closest", "clamp")
    const rgb = separateRgb([indexTexel, "color"])
    // Reconstruct the original 0-255 byte values: 8-bit texture channels come back as normalized floats
    // (value/255) - round(channel*255) is exact under point sampling.
    const slotX = mathNode("round", {value1: [mathNode("multiply", {value1: [rgb, "r"]}, {value2: 255}), "value"]})
    const slotY = mathNode("round", {value1: [mathNode("multiply", {value1: [rgb, "g"]}, {value2: 255}), "value"]})
    const mipLevelStored = mathNode("round", {value1: [mathNode("multiply", {value1: [rgb, "b"]}, {value2: 255}), "value"]})

    // Branch B: intra-tile UV, corrected for the stored tile's mip level - a coarser tile covers 2^mipLevel
    // finer grid cells, so the fractional part must be computed at the matching scale. Already in file-row
    // space (from gridUv), so this fraction directly measures distance from the top of the tile's content in
    // file terms - matching how assembleTiles blitted the tile's pixels - with no further flip needed.
    const mipPow = mathNode("power", {value2: [mipLevelStored, "value"]}, {value1: 2})
    const mipShiftScale = mathNode("divide", {value2: [mipPow, "value"]}, {value1: 1})
    const scaledGridUv = vectorMathNode("scale", {vector1: [gridUv, "vector"], scale: [mipShiftScale, "value"]})
    const intraTileUv = vectorMathNode("fraction", {vector1: [scaledGridUv, "vector"]})

    // 3. Atlas addressing: slot pixel origin + margin offset + within-tile pixel offset, normalized by the
    // atlas's own (dynamic, per-render) pixel dimensions. finalAtlasUv only ever addresses each slot's
    // interior content region - the margin exists purely so the GPU's own filtering neighborhood doesn't
    // bleed into a neighboring slot, never because this math deliberately reaches into it. Still file-row
    // space throughout.
    const slotXY = combineXyz([slotX, "value"], [slotY, "value"], 0)
    const slotOriginPx = vectorMathNode("multiply", {vector1: [slotXY, "vector"]}, {vector2: [slotSize.width, slotSize.height, 1]})
    const contentOriginPx = vectorMathNode("add", {vector1: [slotOriginPx, "vector"]}, {vector2: [descriptor.tileMargin, descriptor.tileMargin, 0]})
    const contentPx = vectorMathNode("multiply", {vector1: [intraTileUv, "vector"]}, {vector2: [descriptor.tileWidth, descriptor.tileHeight, 1]})
    const atlasPx = vectorMathNode("add", {vector1: [contentOriginPx, "vector"], vector2: [contentPx, "vector"]})
    const atlasPixelSizeDivideNode = vectorMathNode("divide", {vector1: [atlasPx, "vector"]}, {vector2: [atlasPixelWidth, atlasPixelHeight, 1]})
    // Explicit compensating flip for the tracked node's own (atlas) sample - same reasoning as indexMapUv
    // above: every image_texture node Cycles evaluates re-flips Y independently, confirmed directly in
    // Cycles' own compiled image-texture OSL (`flip_v = 1.0 - v`).
    const atlasUvSep: RenderNodes.ShaderNode = {type: "separate_xyz", inputs: {vector: [atlasPixelSizeDivideNode, "vector"]}, parameters: {}}
    const flippedAtlasV = mathNode("subtract", {value2: [atlasUvSep, "y"]}, {value1: 1})
    const finalAtlasUv = combineXyz([atlasUvSep, "x"], [flippedAtlasV, "value"], 0)

    // 4. Rewire the tracked node's own vector input to the computed atlas UV. Its own interpolation is left
    // as originally compiled (bilinear-capable is safe now that margin-padded content is actually present
    // in the atlas); extension is defensively forced to "clamp" since the computed UV should never
    // legitimately leave the atlas's [0,1] space.
    node.inputs.vector = [finalAtlasUv, "vector"]
    node.parameters.extension = "clamp"
}

type TiledTextureDescriptor = {
    textureId: number
    imageWidth: number
    imageHeight: number
    tileWidth: number
    tileHeight: number
    tileMipLevels: number
}

type FeedbackRenderOptions = {
    /** Original render graph to create feedback pass from */
    renderGraph: RenderNodes.Render
    /** Map of dataObjectId to tiled texture descriptors */
    tiledTextures: Map<number, TiledTextureDescriptor>
    // Scales the feedback pass's render resolution (e.g. 0.1 renders at 1/10th the beauty pass's pixel
    // dimensions, matching the real-time three.js tile updater's TILE_REQUEST_BUFFER_SIZE_SCALE) - a
    // sparser feedback render is far cheaper (CPU-only OSL path) while still requesting every tile the
    // full-resolution render would, since it's the derivative-based mip level (corrected below for this
    // same scale), not raster position, that drives tile selection. Every pixel decodes independently
    // (see decodeTileRequests), so downsampling only reduces sample count, never correctness.
    viewportScale?: number
    // The maximum number of screen pixels a single texel is allowed to cover - see the RenderWithTilesOptions
    // comment. Unlike viewportScale, this does NOT affect the feedback pass's render resolution below, only
    // the mip-level pick inside the feedback shader (threaded through replaceMaterialsWithFeedback).
    maxPixelsPerTexel?: number
    // Caps the finest tile mip level requested - see the RenderWithTilesOptions comment. Threaded through
    // replaceMaterialsWithFeedback like maxPixelsPerTexel.
    maxTextureResolution?: number
}

function createFeedbackRenderGraph(options: FeedbackRenderOptions): RenderNodes.Render {
    const {renderGraph, tiledTextures, viewportScale = 1.0, maxPixelsPerTexel = 1.0, maxTextureResolution = 0} = options

    // Clone the scene but replace all materials with feedback shaders
    const feedbackScene: RenderNodes.Scene = {
        environment: undefined, // no environment in feedback pass
        lights: [], // no lights in feedback pass
        camera: {
            ...renderGraph.scene.camera,
            // Feedback pixels encode discrete tile-request IDs via OSL emission, not shaded
            // color - any non-neutral camera setting that scales or perturbs the sampled value
            // corrupts the decoded IDs. Override rather than inherit from the beauty-pass camera:
            exposure: 1, // neutral Cycles film exposure multiplier (1 = unity gain, no scaling of emitted IDs)
            fStop: 512, // effectively disables depth-of-field so rays always sample the pixel center, not a blurred lens point
        },
        objects: renderGraph.scene.objects.map((obj) =>
            replaceMaterialsWithFeedback(obj, tiledTextures, viewportScale, maxPixelsPerTexel, maxTextureResolution),
        ),
    }
    return {
        type: "render",
        schemaVersion: renderGraph.schemaVersion,
        session: {
            ...renderGraph.session,
            options: {
                ...renderGraph.session.options,
                render_purpose: "feedback", // identity is the one thing this pass must not inherit
                gpu: false, // use CPU because of OSL
                transparent_background: false,
                use_denoising: false, // no denoising for feedback
                use_osl: true, // need OSL for feedback shaders
                passes: ["Combined"],
                threads: renderGraph.session.options.threads,
                // Feedback pixels encode discrete tile-request IDs, not shaded color - a wide
                // reconstruction filter (e.g. the default blackman_harris) blends neighboring
                // samples' encoded values together, producing non-integer decodes. Box filter
                // with width 1 maps each sample to exactly one pixel with no cross-pixel mixing.
                image_filter_type: "box",
                image_filter_width: 1,
            },
        },
        scene: feedbackScene,
        // Scaled down (uniformly, to preserve aspect ratio) rather than left at the beauty pass's full
        // resolution - see the viewportScale comment on FeedbackRenderOptions above.
        width: Math.max(1, Math.round(renderGraph.width * viewportScale)),
        height: Math.max(1, Math.round(renderGraph.height * viewportScale)),
        samples: 1,
    }
}

// Reduce subdivision for the feedback pass: only a coarse texture-derivative estimate is needed here,
const feedbackSubdivisionLevelReduction = 2

function replaceMaterialsWithFeedback(
    obj: RenderNodes.Object,
    tiledTextures: Map<number, TiledTextureDescriptor>,
    viewportScale: number,
    maxPixelsPerTexel: number,
    maxTextureResolution: number,
): RenderNodes.Object {
    if (obj.type === "meshInstances") {
        return {
            ...obj,
            mesh: replaceMaterialsWithFeedback(obj.mesh, tiledTextures, viewportScale, maxPixelsPerTexel, maxTextureResolution) as RenderNodes.Mesh,
        }
    }

    // It's a Mesh
    const mesh = obj as RenderNodes.Mesh
    const feedbackShaders: Record<string, RenderNodes.ShaderNode> = {}

    // For each material slot, create a feedback shader
    for (const [slotName, shader] of Object.entries(mesh.shaders)) {
        feedbackShaders[slotName] = createFeedbackShaderForMaterial(shader, tiledTextures, viewportScale, maxPixelsPerTexel, maxTextureResolution)
    }

    return {
        ...mesh,
        shaders: feedbackShaders,
        adaptiveSubdivisionRate: undefined, // no adaptive dicing needed for the feedback pass
        meshData: reduceMeshSubdivisionLevel(mesh.meshData, feedbackSubdivisionLevelReduction),
    }
}

// Geometry expressions are DAGs with shared sub-expressions; the memo keeps that sharing, as a plain tree
// copy grows exponentially with nesting depth.
export type ReducedNodes = Map<object, object>

function reduceMeshSubdivisionLevel(mesh: MeshNodes.Mesh, reduction: number, reduced: ReducedNodes = new Map()): MeshNodes.Mesh {
    return memoize(reduced, mesh, () => {
        switch (mesh.type) {
            case "subdivide":
                return {
                    ...mesh,
                    levels: Math.max(0, mesh.levels - reduction),
                    input: reduceMeshSubdivisionLevel(mesh.input, reduction, reduced),
                }
            case "geomGraph":
                return {...mesh, graph: reduceGeometryExprSubdivision(mesh.graph, reduction, reduced)}
            case "loadMesh":
            case "empty":
            case "plane":
                return mesh
        }
    })
}

function reduceGeometryExprSubdivision(expr: MeshNodes.GeometryExpr, dec: number, reduced: ReducedNodes): MeshNodes.GeometryExpr {
    return memoize(reduced, expr, () => {
        // GeometryOperator.op is a plain `string`, so it isn't distinguishable from the "meshToGeom" literal via
        // control-flow narrowing alone - cast explicitly once we've checked the discriminant ourselves.
        if (expr.op === "meshToGeom") {
            const meshToGeom = expr as MeshNodes.MeshToGeometry
            return {...meshToGeom, args: [reduceMeshSubdivisionLevel(meshToGeom.args[0], dec, reduced)]}
        }
        const operator = expr as MeshNodes.GeometryOperator
        return {
            ...operator,
            args: operator.args.map((arg) => reduceGeometryExprArg(arg, dec, reduced)),
        }
    })
}

function reduceGeometryExprArg(arg: MeshNodes.GeometryExprArg, dec: number, reduced: ReducedNodes): MeshNodes.GeometryExprArg {
    if (Array.isArray(arg)) {
        return memoize(reduced, arg, () => arg.map((a) => reduceGeometryExprArg(a, dec, reduced)))
    }
    if (typeof arg === "object" && arg !== null) {
        return reduceGeometryExprSubdivision(arg, dec, reduced)
    }
    return arg // number | string, passed through
}

function memoize<T extends object>(reduced: ReducedNodes, source: T, reduce: () => T): T {
    const existing = reduced.get(source)
    if (existing) return existing as T
    const result = reduce()
    reduced.set(source, result)
    return result
}

// Clones a shader node graph, deduping shared nodes by identity via the same memo pattern as
// reduceMeshSubdivisionLevel above: a node reachable from more than one parent (e.g. a texture node shared
// by two materials) is cloned exactly once, and every parent's clone ends up pointing at that same single
// clone - the DAG's sharing is preserved, not flattened into separate copies.
export function cloneShaderNode(node: RenderNodes.ShaderNode, memo: ReducedNodes): RenderNodes.ShaderNode {
    return memoize(memo, node, () => {
        const clone: RenderNodes.ShaderNode = {...node, parameters: {...node.parameters}, inputs: {}}
        for (const [slot, input] of Object.entries(node.inputs)) {
            clone.inputs[slot] = input ? [cloneShaderNode(input[0], memo), input[1]] : undefined
        }
        return clone
    })
}

// Returns a render graph whose shader graphs (everything reachable from Mesh.shaders / MeshInstances.mesh
// .shaders / Light.shader / Scene.environment) are clones, so downstream graph surgery (applyAtlasLookupSurgery)
// can freely mutate them without ever touching a ShaderNode object that scene-graph-to-render-graph.ts's
// materialCache might hand out again on a later call. Does NOT mutate the input `renderGraph` (or anything
// reachable from it) - callers must render the RETURNED graph, not the one they passed in. Only the
// containers on the path to a cloned node are themselves shallow-copied (structural sharing, like an
// immutable-update pattern); everything untouched (camera, meshData, transforms, non-tiled meshes) is still
// referenced from the original, not deep-cloned. Callers that need to find a specific node in the result
// (e.g. to mutate it) should re-gather from the returned graph (e.g. via gatherImageTextureNodeRefs) rather
// than trying to map a pre-clone node reference to its clone.
export function cloneMaterialShaderGraphs(renderGraph: RenderNodes.Render): RenderNodes.Render {
    const memo: ReducedNodes = new Map()
    const cloneMeshShaders = (mesh: RenderNodes.Mesh): RenderNodes.Mesh => {
        const shaders: Record<string, RenderNodes.ShaderNode> = {}
        for (const [slot, shader] of Object.entries(mesh.shaders)) {
            shaders[slot] = cloneShaderNode(shader, memo)
        }
        return {...mesh, shaders}
    }
    const objects = renderGraph.scene.objects.map(
        (object): RenderNodes.Object => (object.type === "meshInstances" ? {...object, mesh: cloneMeshShaders(object.mesh)} : cloneMeshShaders(object)),
    )
    const lights = renderGraph.scene.lights.map(
        (light): RenderNodes.Light => ("shader" in light && light.shader ? {...light, shader: cloneShaderNode(light.shader, memo)} : light),
    )
    const environment = renderGraph.scene.environment ? cloneShaderNode(renderGraph.scene.environment, memo) : renderGraph.scene.environment
    return {
        ...renderGraph,
        scene: {
            ...renderGraph.scene,
            objects,
            lights,
            environment,
        },
    }
}

type ShaderTiledTextureDescriptor = TiledTextureDescriptor & {
    uvInput: [RenderNodes.ShaderNode, string]
}

type ShaderRayBehavior = {
    reflectionWeight: number // 0.0-1.0: probability of reflection
    refractionWeight: number // 0.0-1.0: probability of refraction
    // implicitly: surfaceWeight = 1.0 - reflectionWeight - refractionWeight
    ior: number // Index of refraction (default: 1.45 for glass)
    roughness: number // 0.0 = mirror, 1.0 = fully rough
    useFresnelBlend: boolean // If true, compute Fresnel instead of using fixed weights
}

function analyzeShaderRayBehavior(shader: RenderNodes.ShaderNode): ShaderRayBehavior {
    const visitedNodes = new Set<RenderNodes.ShaderNode>()
    const rayBehaviorByNode = new Map<RenderNodes.ShaderNode, ShaderRayBehavior>()
    const traverse = (shader: RenderNodes.ShaderNode): ShaderRayBehavior => {
        const compute = (shader: RenderNodes.ShaderNode): ShaderRayBehavior => {
            // Helper to extract numeric value from parameter or input
            const extractNumericValue = (params: any, inputs: any, key: string, defaultValue: number): number => {
                // Check parameter first
                const param = params?.[key]
                if (param !== undefined && typeof param === "number") {
                    return param
                }
                // Check input connection
                const input = inputs?.[key]
                if (input) {
                    const [inputNode, _outputName] = input
                    if (inputNode.type === "value") {
                        const value = inputNode.parameters?.value
                        if (value !== undefined && typeof value === "number") {
                            return value
                        }
                    }
                }
                return defaultValue
            }

            // Handle different BSDF types
            if (shader.type === "transparent_bsdf") {
                // Pure transparency - treat as refraction with IOR=1.0 (no bending)
                return {reflectionWeight: 0, refractionWeight: 1.0, ior: 1.0, roughness: 0.0, useFresnelBlend: false}
            }

            if (shader.type === "glass_bsdf") {
                // Glass: reflection + refraction with Fresnel
                const ior = extractNumericValue(shader.parameters, shader.inputs, "IOR", 1.45)
                const roughness = extractNumericValue(shader.parameters, shader.inputs, "roughness", 0.0)
                return {reflectionWeight: 0.5, refractionWeight: 0.5, ior, roughness, useFresnelBlend: true}
            }

            if (shader.type === "refraction_bsdf") {
                // Pure refraction
                const ior = extractNumericValue(shader.parameters, shader.inputs, "IOR", 1.45)
                const roughness = extractNumericValue(shader.parameters, shader.inputs, "roughness", 0.0)
                return {reflectionWeight: 0, refractionWeight: 1.0, ior, roughness, useFresnelBlend: false}
            }

            if (shader.type === "glossy_bsdf") {
                // Pure reflection (glossy or mirror)
                const roughness = extractNumericValue(shader.parameters, shader.inputs, "roughness", 0.2)
                return {reflectionWeight: 1.0, refractionWeight: 0, ior: 1.45, roughness, useFresnelBlend: false}
            }

            if (shader.type === "principled_bsdf") {
                // Complex material - check multiple parameters
                const transmission = extractNumericValue(shader.parameters, shader.inputs, "transmission_weight", 0.5) // assume transmission if it can't be determined
                const metallic = extractNumericValue(shader.parameters, shader.inputs, "metallic", 0.0)
                const roughness = extractNumericValue(shader.parameters, shader.inputs, "roughness", 0.2)
                const ior = extractNumericValue(shader.parameters, shader.inputs, "ior", 1.45)
                const alpha = extractNumericValue(shader.parameters, shader.inputs, "alpha", 0.5) // assume alpha if it can't be determined

                // If transmission > 0, it's glass-like
                if (transmission > 0) {
                    return {reflectionWeight: 0.5, refractionWeight: 0.5, ior, roughness, useFresnelBlend: true}
                }

                // If transparent, treat as refraction with IOR=1.0
                if (alpha < 1.0) {
                    return {reflectionWeight: 0, refractionWeight: 1.0, ior: 1.0, roughness: 0.0, useFresnelBlend: false}
                }

                // Default: no special ray behavior
                return {reflectionWeight: 0, refractionWeight: 0, ior, roughness, useFresnelBlend: false}
            }

            if (shader.type === "mix_shader" || shader.type === "add_shader") {
                // Combine behaviors from inputs
                const behaviors: ShaderRayBehavior[] = []
                for (const input of Object.values(shader.inputs)) {
                    if (input) {
                        const [inputNode, _outputName] = input
                        const behavior = traverse(inputNode)
                        if (behavior.reflectionWeight > 0 || behavior.refractionWeight > 0) {
                            behaviors.push(behavior)
                        }
                    }
                }

                if (behaviors.length === 0) {
                    return {reflectionWeight: 0, refractionWeight: 0, ior: 1.45, roughness: 0.2, useFresnelBlend: false}
                }

                // Average the behaviors (simple approach)
                const combined: ShaderRayBehavior = {
                    reflectionWeight: 0,
                    refractionWeight: 0,
                    ior: 1.45,
                    roughness: 0.2,
                    useFresnelBlend: false,
                }

                for (const behavior of behaviors) {
                    combined.reflectionWeight = Math.max(combined.reflectionWeight, behavior.reflectionWeight)
                    combined.refractionWeight = Math.max(combined.refractionWeight, behavior.refractionWeight)
                    if (behavior.useFresnelBlend) {
                        combined.useFresnelBlend = true
                    }
                    // Use IOR and roughness from first behavior with refraction/reflection
                    if (behavior.reflectionWeight > 0 || behavior.refractionWeight > 0) {
                        combined.ior = behavior.ior
                        combined.roughness = behavior.roughness
                    }
                }

                return combined
            }

            if (shader.type === "material_output") {
                // Recursively check the surface input
                const surfaceInput = shader.inputs.surface
                if (surfaceInput) {
                    const [surfaceNode, _outputName] = surfaceInput
                    return traverse(surfaceNode)
                }
            }

            // For other node types, recursively check inputs
            for (const input of Object.values(shader.inputs)) {
                if (input) {
                    const [inputNode, _outputName] = input
                    const behavior = traverse(inputNode)
                    if (behavior.reflectionWeight > 0 || behavior.refractionWeight > 0) {
                        return behavior
                    }
                }
            }

            // Default: no special ray behavior
            return {reflectionWeight: 0, refractionWeight: 0, ior: 1.45, roughness: 0.2, useFresnelBlend: false}
        }

        const stored = rayBehaviorByNode.get(shader)
        if (stored) {
            return stored
        }
        if (visitedNodes.has(shader)) {
            throw new Error("Cycle detected in shader graph")
        }
        visitedNodes.add(shader)
        const rayBehavior = compute(shader)
        rayBehaviorByNode.set(shader, rayBehavior)
        return rayBehavior
    }

    return traverse(shader)
}

function createFeedbackShaderForMaterial(
    originalShader: RenderNodes.ShaderNode,
    tiledTextureByDataObjectId: Map<number, TiledTextureDescriptor>,
    viewportScale: number,
    maxPixelsPerTexel: number,
    maxTextureResolution: number,
): RenderNodes.ShaderNode {
    // Find all texture nodes in the material
    const textureNodes = findTextureNodesInShader(originalShader)

    // Filter to only tiled textures
    const shaderTiledTextureDescriptors = textureNodes
        .map((node): ShaderTiledTextureDescriptor | undefined => {
            if (node.type !== "image_texture") {
                return undefined
            }
            const dataRef = node.resources?.image
            if (!dataRef || dataRef.type !== "loadImage") {
                return undefined
            }
            const tiledTextureDescriptor = tiledTextureByDataObjectId.get(dataRef.data.dataObjectId)
            if (!tiledTextureDescriptor) {
                return undefined
            }
            const uvInput = getTextureUvInput(node) ?? [createDefaultUvNode(), "UV"]
            return {
                ...tiledTextureDescriptor,
                uvInput,
            }
        })
        .filter(IsDefined)

    // Analyze the shader's ray behavior
    const rayBehavior = analyzeShaderRayBehavior(originalShader)

    // Create feedback shader
    const feedbackShader = createTextureTileFeedbackShader({
        shaderTiledTextureDescriptors,
        rayBehavior,
        viewportScale,
        maxPixelsPerTexel,
        maxTextureResolution,
    })

    return createFeedbackMaterialOutput(feedbackShader)
}

function findTextureNodesInShader(shader: RenderNodes.ShaderNode, visited = new Set<RenderNodes.ShaderNode>()): RenderNodes.ShaderNode[] {
    if (visited.has(shader)) return []
    visited.add(shader)

    const textureNodes: RenderNodes.ShaderNode[] = []

    // Check if this is a texture node
    if (shader.type === "image_texture") {
        textureNodes.push(shader)
    }

    // Recursively check inputs
    for (const input of Object.values(shader.inputs)) {
        if (input) {
            const [inputNode, _outputName] = input
            textureNodes.push(...findTextureNodesInShader(inputNode, visited))
        }
    }

    return textureNodes
}

function getTextureUvInput(textureNode: RenderNodes.ShaderNode): [RenderNodes.ShaderNode, string] | undefined {
    if (textureNode.type === "image_texture") {
        // Safe to read directly: the feedback pass always runs against the original, pristine graph, before
        // any atlas surgery has touched it - see the comment above applyAtlasLookupSurgery.
        return textureNode.inputs.vector
    }
    return undefined
}

const defaultUvNode: RenderNodes.ShaderNode = {
    type: "texture_coordinate",
    inputs: {},
    parameters: {},
}

function createDefaultUvNode(): RenderNodes.ShaderNode {
    return defaultUvNode
}

function createTextureTileFeedbackShader(args: {
    shaderTiledTextureDescriptors: ShaderTiledTextureDescriptor[]
    rayBehavior: ShaderRayBehavior
    viewportScale?: number
    maxPixelsPerTexel?: number
    maxTextureResolution?: number
}): RenderNodes.ShaderNode {
    const {shaderTiledTextureDescriptors, rayBehavior, viewportScale = 1.0, maxPixelsPerTexel = 1.0, maxTextureResolution = 0} = args
    const oslData = getOslData(shaderTiledTextureDescriptors, rayBehavior)
    return {
        type: "script",
        resources: {
            osl: oslData.oslSource,
        },
        inputs: oslData.inputData,
        parameters: {
            // OSL shader parameters
            viewportScale: viewportScale,
            maxPixelsPerTexel: maxPixelsPerTexel,
            maxTextureResolution: maxTextureResolution,
            ...oslData.parameterData,
        },
    }
}

type OslData = {
    oslSource: RenderNodes.OslSource
    inputData: Record<string, [RenderNodes.ShaderNode, string]>
    parameterData: Record<string, unknown>
}

// use a cache to avoid regenerating identical OSL code (which would bloat the render graph as very often we will use the same source for many shaders with different parameters)
const oslSourceCache: Map<string, RenderNodes.OslSource> = new Map()

function getOslData(shaderTiledTextureDescriptors: ShaderTiledTextureDescriptor[], rayBehavior: ShaderRayBehavior): OslData {
    // Cache key only depends on texture count - all other settings are parameters
    const key = `textureTileFeedback_${shaderTiledTextureDescriptors.length}`
    let oslSource = oslSourceCache.get(key)
    if (!oslSource) {
        const hasTiledTextures = shaderTiledTextureDescriptors.length > 0
        // Ray behavior code that handles reflection and refraction
        const rayReflectionCode = `
            float thisSurfaceSampleWeight = ${hasTiledTextures ? "0.5" : "0.0"};    // weight for surface sampling (if no tiled textures, never sample surface)
            float totalReflectRefractWeight = reflectionWeight + refractionWeight;
            float totalWeight = thisSurfaceSampleWeight + totalReflectRefractWeight;
            if (totalWeight <= 1e-8) {
                result = color(0, 0, 0) * emission();   // no reflection/refraction/surface - output black
                return;
            }
            if (totalReflectRefractWeight > 0.0) {
                float normalizedReflectionWeight, normalizedRefractionWeight;
                if (useFresnelBlend > 0.5) {
                    // Overwrite reflection and refraction by Fresnel
                    fresnel(I, N, ior, normalizedReflectionWeight, normalizedRefractionWeight);
                } else {
                    normalizedReflectionWeight = reflectionWeight / totalReflectRefractWeight;
                    normalizedRefractionWeight = refractionWeight / totalReflectRefractWeight;
                }
                normalizedReflectionWeight *= 1.0 - thisSurfaceSampleWeight;
                normalizedRefractionWeight *= 1.0 - thisSurfaceSampleWeight;
                float rand = cellnoise(u * 1433642.0, v * 1123523.0);
                if (rand < normalizedReflectionWeight) {
                    // Reflection
                    /*if (roughness > 0.01) {
                        result = microfacet("ggx", N, N, roughness, roughness, 0.0, 0);
                    } else {
                        result = reflection(N);
                    }*/
                    // vector R = reflect(I, N);
                    // result = trace(R);
                    result = reflection(N);
                    return;
                } else if (rand < normalizedReflectionWeight + normalizedRefractionWeight) {
                    // Refraction
                    /*if (roughness > 0.01) {
                        result = microfacet("ggx", N, N, roughness, roughness, ior, 1);
                    } else {
                        result = refraction(N, ior);
                    }*/
                    // vector T = refract(I, N, ior);
                    // result = trace(T);
                    result = transparent(); // refraction() applies fresnel scaling, but we want pure refraction here, which is not available it seems.
                    return;
                }
            }
        `
        // array parameters don't work properly so we do this workaround
        let parameterDeclaration = `
            // Ray behavior parameters
            float reflectionWeight = 0,
            float refractionWeight = 0,
            float ior = 1.45,
            float roughness = 0.2,
            float useFresnelBlend = 0,
        `

        let surfaceSampling = ""
        if (!hasTiledTextures) {
            surfaceSampling = `
                result = color(0, 0, 0) * emission();   // no textures - output black
            `
        } else {
            let parameterSelection = `
                    point uvPoint = point(0, 0, 0);
                    int textureId = -1;
                    int numMipLevels = 1;
                    float textureSizeX = 1;
                    float textureSizeY = 1;
                    float tileSizeX = 1;
                    float tileSizeY = 1;
                    float rand = cellnoise(u * 100000.0, v * 100000.0);
                    int randomIndex = min(int(rand * ${shaderTiledTextureDescriptors.length}.0), ${shaderTiledTextureDescriptors.length} - 1);
                `
            for (let i = 0; i < shaderTiledTextureDescriptors.length; i++) {
                parameterDeclaration += `
                point uvPoint${i} = point(u, v, 0),
                int textureId${i} = -1,
                int numMipLevels${i} = 0,
                float textureSizeX${i} = 0,
                float textureSizeY${i} = 0,
                float tileSizeX${i} = 0,
                float tileSizeY${i} = 0,
            `
                parameterSelection += `
                ${i > 0 ? "else" : ""} if (randomIndex == ${i}) {
                    uvPoint = uvPoint${i};
                    textureId = textureId${i};
                    numMipLevels = numMipLevels${i};
                    textureSizeX = textureSizeX${i};
                    textureSizeY = textureSizeY${i};
                    tileSizeX = tileSizeX${i};
                    tileSizeY = tileSizeY${i};
                }
            `
            }
            surfaceSampling = `
                ${parameterSelection}
                
                // Extract UV and flip Y for top-left origin
                float uvX = uvPoint[0];
                float uvY = 1.0 - uvPoint[1];
                
                // Compute derivatives in texture space
                float ddxX = Dx(uvX) * textureSizeX;
                float ddxY = Dx(uvY) * textureSizeY;
                float ddyX = Dy(uvX) * textureSizeX;
                float ddyY = Dy(uvY) * textureSizeY;
            
                // Compute mip level using smallest singular value of Jacobian
                float a = ddxX * ddxX + ddxY * ddxY;
                float b = ddxX * ddyX + ddxY * ddyY;
                float c = ddyX * ddyX + ddyY * ddyY;
                float a_m_c = a - c;
                float lambda_min = 0.5 * (a + c - sqrt(a_m_c * a_m_c + 4.0 * b * b));
                float rho = sqrt(lambda_min);

                // Cap the finest mip level requested to a target pixel resolution (0 = uncapped), mirroring
                // the real-time three.js tiled texture manager's minMipLevel.
                int minMipLevel = maxTextureResolution > 0
                    ? max(0, numMipLevels - (int)ceil(log(maxTextureResolution / max(tileSizeX, tileSizeY), 2) + 1))
                    : 0;
                int mipLevel = (int)clamp(floor(log(rho * viewportScale * maxPixelsPerTexel, 2)), minMipLevel, numMipLevels - 1);
            
                // Compute tile indices
                float numTilesX = textureSizeX / tileSizeX;
                float numTilesY = textureSizeY / tileSizeY;
                float mipScale = 1.0 / (float)(1 << mipLevel);
            
                // Wrap UV to [0, 1)
                float wrappedX = fmod(uvX, 1.0);
                float wrappedY = fmod(uvY, 1.0);
                if (wrappedX < 0) wrappedX += 1.0;
                if (wrappedY < 0) wrappedY += 1.0;
            
                int tileX = (int)floor(wrappedX * numTilesX * mipScale);
                int tileY = (int)floor(wrappedY * numTilesY * mipScale);
                
                float out_r, out_g, out_b;
                if (tileX < 0 || tileX >= 255 || tileY < 0 || tileY >= 255 || mipLevel < 0 || mipLevel > 15 || textureId < 0 || textureId > 4095) {
                    // out of range - output error
                    out_r = 1.0;
                    out_g = 1.0;
                    out_b = 1.0;
                }
                else
                {
                    out_r =  (float)textureId / 4095.0; // 12-bit texture ID
                    out_g =  (float)mipLevel / 15.0;   // 4-bit mip level
                    out_b =  (float)((tileY << 8) | tileX) / 65535.0; // 8-bit tileY + 8-bit tileX
                }
                // Output as unshaded emission
                result = color(out_r, out_g, out_b) * emission();
            `
        }
        const oslSourceCode = `
        shader textureTileFeedback(
            // Inputs
            float viewportScale = 1,
            float maxPixelsPerTexel = 1,
            float maxTextureResolution = 0,
            ${parameterDeclaration}

            // Output
            output closure color result = 0
        )
        {
            ${rayReflectionCode}
            ${surfaceSampling}
        }
        `
        oslSource = {
            type: "oslSource",
            name: "texture-tile-feedback",
            source: oslSourceCode,
        }
        oslSourceCache.set(key, oslSource)
    }
    const inputData: Record<string, [RenderNodes.ShaderNode, string]> = {}
    const parameterData: Record<string, unknown> = {}

    // Add ray behavior parameters
    parameterData.reflectionWeight = rayBehavior.reflectionWeight
    parameterData.refractionWeight = rayBehavior.refractionWeight
    parameterData.ior = rayBehavior.ior
    parameterData.roughness = rayBehavior.roughness
    parameterData.useFresnelBlend = rayBehavior.useFresnelBlend ? 1.0 : 0.0

    // Add texture parameters
    for (let i = 0; i < shaderTiledTextureDescriptors.length; i++) {
        inputData[`uvPoint${i}`] = shaderTiledTextureDescriptors[i].uvInput
        parameterData[`textureId${i}`] = shaderTiledTextureDescriptors[i].textureId
        parameterData[`numMipLevels${i}`] = shaderTiledTextureDescriptors[i].tileMipLevels
        parameterData[`textureSizeX${i}`] = shaderTiledTextureDescriptors[i].imageWidth
        parameterData[`textureSizeY${i}`] = shaderTiledTextureDescriptors[i].imageHeight
        parameterData[`tileSizeX${i}`] = shaderTiledTextureDescriptors[i].tileWidth
        parameterData[`tileSizeY${i}`] = shaderTiledTextureDescriptors[i].tileHeight
    }
    return {
        oslSource,
        inputData,
        parameterData,
    }
}

type TileRequest = {
    textureId: number
    mipLevel: number
    tileX: number
    tileY: number
}

function decodeTileRequests(typedImageData: TypedImageData, maxTextureId: number, logger: CmLogger): TileRequest[] {
    if (typedImageData.dataType !== "float32") {
        throw Error("Decoded feedback image has unexpected data type")
    }
    if (typedImageData.channelLayout !== "RGBA") {
        throw Error("Decoded feedback image has unexpected channel layout")
    }
    const pixelData = new Float32Array(typedImageData.data.buffer, typedImageData.data.byteOffset, typedImageData.data.byteLength / 4)
    const requests = new Map<string, TileRequest>()
    let hasError = false
    // limit error reporting
    const maxLoggedPerIssue = 100
    let nonIntegerCount = 0
    let invalidTextureIdCount = 0
    const rescale = (normalizedValue: number, scale: number, x: number, y: number) => {
        const rescaledValue = normalizedValue * scale
        const roundedValue = Math.round(rescaledValue)
        if (Math.abs(roundedValue - rescaledValue) > 1e-3) {
            nonIntegerCount++
            if (nonIntegerCount <= maxLoggedPerIssue) {
                logger.warn(
                    `Decoded value ${rescaledValue} at (${x}, ${y}) appears to be a non-integer value, suggesting mixing of multiple texture requests in a single pixel.`,
                )
            }
            hasError = true
        }
        return roundedValue
    }
    let i = 0
    for (let y = 0; y < typedImageData.height; y++) {
        for (let x = 0; x < typedImageData.width; x++) {
            const r = pixelData[i]
            const g = pixelData[i + 1]
            const b = pixelData[i + 2]
            i += 4
            // Skip black pixels (no request)
            if (r === 0 && g === 0 && b === 0) {
                continue
            }
            // Skip error pixels
            if (r === 1) {
                hasError = true
                continue
            }

            // Decode
            const b_int = rescale(b, 65535, x, y)
            const tileX = b_int & 0xff
            const tileY = b_int >> 8
            const mipLevel = rescale(g, 15, x, y)
            const textureId = rescale(r, 4095, x, y)
            const key = `${textureId}:${mipLevel}:${tileX}:${tileY}`
            if (!requests.has(key)) {
                if (textureId <= 0 || textureId > maxTextureId) {
                    invalidTextureIdCount++
                    if (invalidTextureIdCount <= maxLoggedPerIssue) {
                        logger.warn(`Invalid textureId (${textureId}) in feedback buffer at pixel (${x}, ${y})`)
                    }
                    hasError = true
                } else {
                    requests.set(key, {textureId, mipLevel, tileX, tileY})
                }
            }
        }
    }
    if (nonIntegerCount > maxLoggedPerIssue) {
        logger.warn(
            `Texture tile feedback: ${nonIntegerCount - maxLoggedPerIssue} additional non-integer pixel value(s) suppressed (${nonIntegerCount} total).`,
        )
    }
    if (invalidTextureIdCount > maxLoggedPerIssue) {
        logger.warn(
            `Texture tile feedback: ${invalidTextureIdCount - maxLoggedPerIssue} additional invalid textureId pixel(s) suppressed (${invalidTextureIdCount} total).`,
        )
    }
    if (hasError) {
        logger.error("Texture tile feedback: encountered out-of-range tile request")
    }
    return Array.from(requests.values())
}

function createFeedbackMaterialOutput(feedbackShader: RenderNodes.ShaderNode): RenderNodes.ShaderNode {
    return {
        type: "material_output",
        inputs: {
            surface: [feedbackShader, "result"],
            volume: undefined,
            displacement: undefined,
        },
        parameters: {},
    }
}
